import { isAxiosError } from 'axios'

import { type Api, FileProcessingError } from '@/services/api/api'
import { presentationStylesQuery } from '@/utils/deliveryModes'

export const PROCESSING_POLL_MS = 5000
/**
 * Decision 32 watches processing past `createStep`'s 30 minutes. This ceiling is only for a file the
 * backend never finishes, such as a failed audio extraction, which would otherwise hold a processing
 * slot for the life of the page (Shealyn, 2026-09-24).
 */
export const MAX_PROCESSING_MS = 6 * 60 * 60 * 1000
export const REMOVAL_POLL_MS = 2000
const MAX_REMOVAL_CHECKS = 30
/** A long transcode shouldn't fail a file over one dropped poll. */
const MAX_CONSECUTIVE_POLL_FAILURES = 10

export class AbortedError extends Error {
  constructor() {
    super('Aborted')
    this.name = 'AbortedError'
  }
}

export class ChannelChangedError extends Error {
  constructor() {
    super('The channel changed while the upload was running')
    this.name = 'ChannelChangedError'
  }
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new AbortedError())
    const onAbort = () => {
      clearTimeout(timer)
      reject(new AbortedError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

function throwIfAborted(signal: AbortSignal) {
  if (signal.aborted) throw new AbortedError()
}

/**
 * Signs and sends one file. Called inside the file's upload slot, so the 300-second URL only has
 * to last until the `PUT` starts — SME-1111 decision 22.
 */
export async function uploadFile(
  api: Api,
  file: File,
  signal: AbortSignal,
  onProgress: (percent: number) => void,
): Promise<string> {
  const prepared = await api.uploadPrepare({ fileType: file.type }, signal)
  throwIfAborted(signal)
  await api.upload({ file_path: prepared.filePath, file_content: file }, signal, onProgress)
  throwIfAborted(signal)
  return prepared.filePath
}

/**
 * `api.createStep`'s own poll gives up after 30 minutes and returns as if done. This one waits out a
 * video's transcode too, since a failed transcode is the one failure a video has (`getStepStatus`).
 */
export async function waitForProcessing(
  api: Api,
  stepId: string,
  signal: AbortSignal,
  /** Processing's percentage, on every poll where the backend reports a count. */
  onProgress?: (percent: number) => void,
): Promise<void> {
  const deadline = Date.now() + MAX_PROCESSING_MS
  let failures = 0
  for (;;) {
    await sleep(PROCESSING_POLL_MS, signal)
    if (Date.now() > deadline) throw new FileProcessingError('Processing did not finish')
    let content
    try {
      // Not `getContent`, which downloads the extracted text on every poll once it exists.
      content = await api.getContentMeta({ step_id: stepId })
      failures = 0
    } catch (error) {
      throwIfAborted(signal)
      if (++failures >= MAX_CONSECUTIVE_POLL_FAILURES) throw error
      continue
    }
    throwIfAborted(signal)
    // `/content/get` answers `null` once the step is gone, as when the PM deletes it in Edit CM.
    if (!content) throw new FileProcessingError('The file no longer exists')

    const error = content.processProgress?.error
    if (error) {
      throw new FileProcessingError((typeof error === 'string' ? error : error.message) || 'Failed to process file')
    }
    if (content.inProcess || content.transcodeCompleted === false) {
      // Shown whenever the backend has a count, however coarse. Capped at 99, since a PDF's count can
      // reach its total well before processing ends; either number can be null (`api.ts` pollForProcessing).
      const progress = content.processProgress
      if (content.inProcess && typeof progress?.current === 'number' && progress.total > 0) {
        onProgress?.(Math.min(99, Math.floor((progress.current / progress.total) * 100)))
      }
      continue
    }
    if (content.transcodeSuccess === false) throw new FileProcessingError('Transcode failed')
    return
  }
}

export type ModuleCommit = {
  filePath: string
  fileName: string
  title: string
  eskId: string
  deliveryModeName: string | null
}

/**
 * Decision 46: a write refused because nobody holds the lock takes it back and goes once more. The
 * PM leaving Edit CM, or a Force Unlock nobody followed up, can release the lock a job relies on.
 */
export async function withLockRetry<T>(
  api: Api,
  taskId: string,
  write: () => Promise<T>,
  beforeRetry?: () => void,
): Promise<T> {
  try {
    return await write()
  } catch (error) {
    if (!isNotLocked(error)) throw error
    beforeRetry?.()
    await api.lockContentModule({ task_id: taskId })
    return write()
  }
}

/**
 * Decision 23. The module is created only once the bytes have landed, so a failed upload leaves
 * nothing behind; `guard` runs before each write so a job never commits into a channel it did
 * not start in (48).
 */
export async function commitModule(
  api: Api,
  commit: ModuleCommit,
  signal: AbortSignal,
  hooks: {
    guard: () => void
    onModuleCreated: (taskId: string) => void
    editorHolds: (taskId: string) => boolean
    onProcessing?: (percent: number) => void
  },
): Promise<{ taskId: string; stepId: string }> {
  hooks.guard()
  const { taskId } = await api.createContentModule({ content: ' ' })
  hooks.onModuleCreated(taskId)
  throwIfAborted(signal)

  // Decision 27: the title is in place before anything can generate one. A PM who opens the draft
  // while it processes would otherwise see a blank title, generate one, and save over the staged one.
  // `lock: false` keeps the lock the module was created with.
  hooks.guard()
  await api.updateContentModule({ task_id: taskId, name: commit.title, title: commit.title, topics: [], lock: false })
  throwIfAborted(signal)

  hooks.guard()
  const step = await api.createStep({ task_id: taskId, file_name: commit.fileName, s3_file: commit.filePath }, signal, {
    waitForProcessing: false,
  })
  if (step.inProcess) await waitForProcessing(api, step.stepId, signal, hooks.onProcessing)

  // Whatever the PM saved from Edit CM while the file processed is theirs and stays. `/cm/create`
  // leaves Sidekick and mode empty (`cm.py:212-222`), and the only title written since is the staged one.
  const stored = await api.getContentModule({ task_id: taskId })
  const titleUntouched = [stored.title, stored.name].every(value => !value || value === commit.title)
  const modeName = stored.presentationStyleName ? undefined : commit.deliveryModeName
  // Edit CM validates the mode by its description, which the server doesn't fill in from the name.
  const style = modeName
    ? (await api.queryClient.fetchQuery(presentationStylesQuery(api)).catch(() => [])).find(s => s.name === modeName)
    : undefined
  hooks.guard()
  await withLockRetry(
    api,
    taskId,
    () =>
      api.updateContentModule({
        task_id: taskId,
        ...(titleUntouched && { name: commit.title, title: commit.title }),
        ...(!stored.selectEsk && { select_esk: commit.eskId }),
        ...(modeName && { presentation_style_name: modeName, presentation_style: style?.description }),
        ...((!stored.primaryStepId || stored.primaryStepId === step.stepId) && { primary_step_id: step.stepId }),
        // A topic-restricted expert's update 500s when `topics` is missing (`cm.py:567-572` iterates it).
        topics: stored.topics ?? [],
        // The default releases the lock, which a PM who opened the draft in Edit CM is still using.
        ...(hooks.editorHolds(taskId) && { lock: false }),
      }),
    hooks.guard,
  )
  return { taskId, stepId: step.stepId }
}

export type StepCommit = { taskId: string; filePath: string; fileName: string; title: string }

/**
 * A supplementary file on the module being edited. `/step/create` waits its turn behind the
 * module's other step writes and chains from its last step as it is then (decision 52).
 */
export async function commitStep(
  api: Api,
  commit: StepCommit,
  signal: AbortSignal,
  hooks: {
    guard: () => void
    onStepCreated: (stepId: string) => void
    onProcessing?: (percent: number) => void
  },
): Promise<{ stepId: string }> {
  const { taskId } = commit
  hooks.guard()
  // No signal: a create aborted mid-request can still land, and would leave a step nothing knows about.
  const step = await withLockRetry(
    api,
    taskId,
    () =>
      api.createStep({ task_id: taskId, file_name: commit.fileName, s3_file: commit.filePath }, undefined, {
        waitForProcessing: false,
        afterLastStep: true,
      }),
    hooks.guard,
  )
  hooks.onStepCreated(step.stepId)
  throwIfAborted(signal)

  // Decision 27. An edited name is never regenerated, but a file parsed in the web worker can be
  // generating already, and its write carries the step as it read it (`sme_manager.py:4206, 4388`).
  const writeTitle = () =>
    withLockRetry(api, taskId, () => api.updateStep({ step_id: step.stepId, name: commit.title }), hooks.guard)
  hooks.guard()
  await writeTitle()
  throwIfAborted(signal)

  if (step.inProcess) await waitForProcessing(api, step.stepId, signal, hooks.onProcessing)

  // Only over the generated title: a PM may have opened the finished file and renamed it. The file is
  // complete by now, so a failed check leaves the generated title rather than failing the file.
  try {
    const stored = await api.getStep({ step_id: step.stepId })
    if (stored.name !== commit.title && (!stored.name || stored.name === stored.titleAi)) {
      hooks.guard()
      await writeTitle()
    }
  } catch (error) {
    if (error instanceof ChannelChangedError) throw error
    console.warn(`[bulkUploadTask] title check failed for ${step.stepId}`, error)
  }
  throwIfAborted(signal)
  return { stepId: step.stepId }
}

/** Decision 32 in supplemental mode: the failed file comes off the module, unlike a single upload. */
export async function removeStep(api: Api, taskId: string, stepId: string): Promise<boolean> {
  try {
    await withLockRetry(api, taskId, () => api.deleteStep({ step_id: stepId, task_id: taskId }))
    return true
  } catch {
    // A step that's already gone answers 500 (`step_repository_mongo_impl.py:471-473`), so read back.
    try {
      const steps = await api.getSteps({ task_id: taskId })
      return !steps.some(s => s.stepId === stepId)
    } catch {
      return false
    }
  }
}

/**
 * `verify_cm_lock`'s refusal when nobody holds the lock. Held by someone else carries `details` and
 * is left to fail: a co-owner has taken the module (plan decision 31).
 */
export function isNotLocked(error: unknown): boolean {
  return lockRefusal(error) === 'unlocked'
}

/** A co-owner holds the module: a write refused as theirs, or `/cm/lock` answering 409. */
export function isModuleTaken(error: unknown): boolean {
  return lockRefusal(error) === 'taken' || (isAxiosError(error) && error.response?.status === 409)
}

function lockRefusal(error: unknown): 'unlocked' | 'taken' | undefined {
  if (!isAxiosError(error) || error.response?.status !== 403) return undefined
  const body: unknown = error.response.data
  if (!Array.isArray(body)) return undefined
  const refusal = body.find((e: { field?: string }) => e?.field === 'locked_by') as { details?: unknown } | undefined
  if (!refusal) return undefined
  return refusal.details === undefined ? 'unlocked' : 'taken'
}

/**
 * The PM's own Delete, then a read-back: `/cm/change_status` queues the change and answers before
 * it runs, so a 200 alone doesn't mean the module is gone — evidence §3.6.
 */
export async function removeModule(api: Api, taskId: string, options = { confirm: true }): Promise<boolean> {
  try {
    await api.changeContentModuleStatus({ task_id: taskId, status: 'removed' })
  } catch {
    return false
  }
  if (!options.confirm) return true
  for (let check = 0; check < MAX_REMOVAL_CHECKS; check++) {
    await sleep(REMOVAL_POLL_MS)
    try {
      const module = await api.getContentModule({ task_id: taskId })
      if (module.status === 'removed') return true
    } catch {
      // Retried on the next check; a module that can't be read back stays on the unfinished list.
    }
  }
  return false
}

export const isAbort = (error: unknown, signal: AbortSignal) =>
  signal.aborted || error instanceof AbortedError || (isAxiosError(error) && error.code === 'ERR_CANCELED')
